import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useApp } from '../contexts/AppContext';
import { wardNames } from '../data/checklists';
import { TrendingUpIcon, AlertCircleIcon, ClockIcon, CheckCircle2Icon, ClipboardCheckIcon, RefreshCwIcon } from 'lucide-react';
interface HomeProps {
  onNavigate: (page: string) => void;
}
export function Home({
  onNavigate
}: HomeProps) {
  const {
    colors
  } = useTheme();
  const {
    currentWard,
    user,
    lastSynced
  } = useApp();
  const [isSyncing, setIsSyncing] = useState(false);
  const mockData = {
    pendingChecklists: 3,
    recentAlerts: 2,
    completedToday: 8,
    completionRate: 92,
    avgResponseTime: 2.3,
    criticalPrompts: 100,
    alerts: [{
      id: '1',
      type: 'critical',
      title: 'Hand hygiene compliance critical',
      ward: 'Surgical Ward',
      time: '10 mins ago'
    }, {
      id: '2',
      type: 'pending',
      title: 'Pre-procedure checklist pending',
      ward: 'Surgical Ward',
      time: '25 mins ago'
    }]
  };
  const handleSyncNow = async () => {
    setIsSyncing(true);
    // Simulate sync operation
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSyncing(false);
    alert('Data synced successfully!');
  };
  const getAlertColor = (type: string) => {
    switch (type) {
      case 'critical':
        return colors.error;
      case 'pending':
        return colors.warning;
      case 'resolved':
        return colors.success;
      default:
        return colors.textSecondary;
    }
  };
  const getAlertBg = (type: string) => {
    switch (type) {
      case 'critical':
        return colors.error + '15';
      case 'pending':
        return colors.warning + '15';
      case 'resolved':
        return colors.success + '15';
      default:
        return colors.divider;
    }
  };
  const formatLastSynced = () => {
    if (!lastSynced) return 'Never';
    const now = new Date();
    const diff = Math.floor((now.getTime() - lastSynced.getTime()) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} mins ago`;
    const hours = Math.floor(diff / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  };
  return <div className="space-y-6 max-w-2xl mx-auto" data-id="element-147">
      {/* Today's Shift Summary */}
      <div className="p-6 rounded-xl shadow-sm" style={{
      backgroundColor: colors.cardSurface
    }} data-id="element-148">
        <h2 className="text-xl font-bold mb-4" data-id="element-149">Today's Shift Summary</h2>
        <div className="space-y-4" data-id="element-150">
          <div className="flex items-center justify-between" data-id="element-151">
            <span style={{
            color: colors.textSecondary
          }} data-id="element-152">
              Pending Checklists
            </span>
            <span className="text-2xl font-bold px-3 py-1 rounded-lg" style={{
            backgroundColor: colors.accent + '20',
            color: colors.accent
          }} data-id="element-153">
              {mockData.pendingChecklists}
            </span>
          </div>
          <div className="flex items-center justify-between" data-id="element-154">
            <span style={{
            color: colors.textSecondary
          }} data-id="element-155">
              Recent Alerts
            </span>
            <span className="text-2xl font-bold px-3 py-1 rounded-lg" style={{
            backgroundColor: colors.warning + '20',
            color: colors.warning
          }} data-id="element-156">
              {mockData.recentAlerts}
            </span>
          </div>
          <div className="flex items-center justify-between" data-id="element-157">
            <span style={{
            color: colors.textSecondary
          }} data-id="element-158">
              Completed Today
            </span>
            <span className="text-2xl font-bold px-3 py-1 rounded-lg" style={{
            backgroundColor: colors.success + '20',
            color: colors.success
          }} data-id="element-159">
              {mockData.completedToday}
            </span>
          </div>
        </div>
      </div>
      {/* Your Compliance */}
      <div className="p-6 rounded-xl shadow-sm" style={{
      backgroundColor: colors.cardSurface
    }} data-id="element-160">
        <div className="flex items-center justify-between mb-4" data-id="element-161">
          <h2 className="text-xl font-bold" data-id="element-162">Your Compliance</h2>
          <TrendingUpIcon size={24} style={{
          color: colors.success
        }} data-id="element-163" />
        </div>
        <div className="space-y-4" data-id="element-164">
          <div data-id="element-165">
            <div className="flex items-center justify-between mb-2" data-id="element-166">
              <span style={{
              color: colors.textSecondary
            }} data-id="element-167">
                Completion Rate
              </span>
              <span className="text-xl font-bold" data-id="element-168">
                {mockData.completionRate}%
              </span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{
            backgroundColor: colors.divider
          }} data-id="element-169">
              <div className="h-full rounded-full transition-all" style={{
              width: `${mockData.completionRate}%`,
              backgroundColor: colors.accent
            }} data-id="element-170" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4" data-id="element-171">
            <div data-id="element-172">
              <div className="text-sm mb-1" style={{
              color: colors.textSecondary
            }} data-id="element-173">
                Avg Response Time
              </div>
              <div className="text-2xl font-bold" data-id="element-174">
                {mockData.avgResponseTime} min
              </div>
            </div>
            <div data-id="element-175">
              <div className="text-sm mb-1" style={{
              color: colors.textSecondary
            }} data-id="element-176">
                Critical Prompts
              </div>
              <div className="text-2xl font-bold" style={{
              color: colors.success
            }} data-id="element-177">
                {mockData.criticalPrompts}%
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Alerts */}
      <div className="p-6 rounded-xl shadow-sm" style={{
      backgroundColor: colors.cardSurface
    }} data-id="element-178">
        <h2 className="text-xl font-bold mb-4" data-id="element-179">Recent Alerts</h2>
        <div className="space-y-3" data-id="element-180">
          {mockData.alerts.map(alert => <div key={alert.id} className="p-4 rounded-lg flex items-start gap-3" style={{
          backgroundColor: getAlertBg(alert.type)
        }} data-id="element-181">
              {alert.type === 'critical' ? <AlertCircleIcon size={24} style={{
            color: getAlertColor(alert.type)
          }} data-id="element-182" /> : <ClockIcon size={24} style={{
            color: getAlertColor(alert.type)
          }} data-id="element-183" />}
              <div className="flex-1" data-id="element-184">
                <div className="font-semibold mb-1" style={{
              color: getAlertColor(alert.type)
            }} data-id="element-185">
                  {alert.title}
                </div>
                <div className="text-sm" style={{
              color: colors.textSecondary
            }} data-id="element-186">
                  {alert.ward} • {alert.time}
                </div>
              </div>
            </div>)}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="space-y-3" data-id="element-187">
        <div className="grid grid-cols-2 gap-3" data-id="element-188">
          <button onClick={() => onNavigate('checklists')} className="p-4 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-sm transition-all hover:shadow-md" style={{
          backgroundColor: colors.accent,
          color: '#FFFFFF'
        }} data-id="element-189">
            <ClipboardCheckIcon size={20} data-id="element-190" />
            Start Checklist
          </button>
          <button onClick={handleSyncNow} disabled={isSyncing} className="p-4 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-sm transition-all hover:shadow-md disabled:opacity-50" style={{
          backgroundColor: colors.success,
          color: '#FFFFFF'
        }} data-id="element-191">
            <RefreshCwIcon size={20} className={isSyncing ? 'animate-spin' : ''} data-id="element-192" />
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
        <div className="text-center text-sm" style={{
        color: colors.textSecondary
      }} data-id="element-193">
          Last synced: {formatLastSynced()}
        </div>
      </div>
    </div>;
}